import React, { memo, useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import { validators } from "investira.sdk";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Icon,
  LoadingButton,
} from "investira.react.components";
import { LoadingMessages } from "../../components/molecules";
import services from "../../services";
import withMessage from "../../hoc/withMessage";
import BenchmarkListItem from "./BenchmarkListItem";

const MESSAGES = [
  "Analisando suas carteiras...",
  "Processando dados financeiros...",
  "Otimizando suas recomendações...",
  "Calculando os melhores benchmarks...",
  "Gerando recomendações personalizadas...",
  "Consultando bases de dados...",
  "Isso pode levar alguns minutos...",
  "Estamos quase lá...",
];

const BenchmarksSugestao = memo((props) => {
  const { benchmarks = [], setBenchmarks } = props;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [carteira, setCarteira] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Novo state para armazenar o valor do select para cada item
  const [selectedOptions, setSelectedOptions] = useState([]);

  const handleStart = (e, pCarteira, pBenchmarks) => {
    e.stopPropagation();
    e.preventDefault();

    setOpen(true);
    setLoading(true);
    setError(null);
    setData([]);

    services.consultas.correlacaoBenchmarks(
      {
        data: {
          consolidacao_carteira_tipo: "P",
          data: dayjs(pCarteira.data_posicao).format("YYYY-MM-DD"),
          dias: 252, //252
          corridos: false,
        },
      },
      (rRes) => {
        const xBenchmarksData = rRes.data;

        const xInitialSeletedOptions = xBenchmarksData.resumo.map((xResumo) => {
          const xIndicador = xBenchmarksData.benchmarks[xResumo.indicador_id];
          const xIndicadoresIds = pBenchmarks.map(
            (xItem) => xItem.indicador_id
          );
          const xCadastrado = xIndicadoresIds.includes(xIndicador.indicador_id);

          return xCadastrado;
        });

        setData(xBenchmarksData);
        setSelectedOptions(xInitialSeletedOptions); // Inicializa selects com vazio
      },
      (rErr) => {
        setError("Erro ao carregar recomendações.");
      },
      () => {
        setLoading(false);
      }
    );
  };

  const handleClose = () => {
    setOpen(false);
    setData([]);
    setError(null);
    setLoading(false);
    setSelectedOptions([]); // Limpa selects ao fechar
    services.consultas.cancel.correlacaoBenchmarks &&
      services.consultas.cancel.correlacaoBenchmarks.cancel();
  };

  const handleStore = (e, pSelectedOptions, pBenchmarks) => {
    e.stopPropagation();
    e.preventDefault();
    // Faz o cruzamento entre pSelectedOptions e pBenchmarks para retornar benchmarks com index selecionado = true
    const xSelecionados = pBenchmarks.filter(
      (xBenchmark, xIndex) => pSelectedOptions[xIndex] === true
    );

    setIsSubmitting(true);

    Promise.all(
      xSelecionados.map((xIndicador) =>
        services.benchmarks.add({
          indicador_id: xIndicador.indicador_id,
          benchmark: xIndicador.indicador,
        })
      )
    ).then(() => {
      // Após adicionar todos, buscar novamente a lista de benchmarks
      services.benchmarks.list({ sort: "benchmark" }, (rResList) => {
        setBenchmarks(rResList.data);
        setIsSubmitting(true);
        handleClose();
      });
    });
    // Aqui pode usar selectedOptions conforme desejado para processar o cadastro
  };

  // Função para atualizar o array do select correspondente
  const handleSelectChange = (pIndex, pValue) => {
    setSelectedOptions((xPrev) => {
      const xNext = [...xPrev];
      xNext[pIndex] = pValue;
      return xNext;
    });
  };

  useEffect(() => {
    services.carteiras.list({ carteira_tipo_id: "P" }, (rRes) => {
      if (rRes?.data[0]) {
        setCarteira(rRes?.data[0]);
      }
    });
  }, []);

  const xBenchmarks = data?.resumo
    ? data.resumo.map((xResumo) => {
        const xIndicador = data.benchmarks[xResumo.indicador_id];
        const xIndicadoresIds = benchmarks.map((xItem) => xItem.indicador_id);
        const xCadastrado = xIndicadoresIds.includes(xIndicador.indicador_id);

        return {
          ...xIndicador,
          ativos: xResumo.ativos,
          cadastrado: xCadastrado,
        };
      })
    : [];

  return (
    <>
      <Stack spacing={0.5} sx={{ alignItems: "start" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={(e) => handleStart(e, carteira, benchmarks)}
          disabled={loading}
          startIcon={<Icon iconName="lamp" size={16} />}
        >
          Recomendar Benchmarks
        </Button>
        <Typography variant="caption" color="textSecondary">
          Com base na composição das suas carteiras
        </Typography>
      </Stack>

      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>Benchmarks Recomendados</DialogTitle>

        <DialogContent>
          {loading ? (
            <Stack alignItems="center" py={2}>
              {/* <CircularProgress /> */}
              <LoadingMessages messages={MESSAGES} loading={loading} />
            </Stack>
          ) : error ? (
            <Typography
              color="error"
              variant="body2"
              alignItems="center"
              py={2}
              textAlign="center"
            >
              {error}
            </Typography>
          ) : validators.isNull(data) || data.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              alignItems="center"
              py={2}
              textAlign="center"
            >
              Nenhum benchmark recomendado encontrado.
            </Typography>
          ) : (
            <Stack>
              {!validators.isEmpty(xBenchmarks) &&
                xBenchmarks.map((xBenchmark, xIndex) => (
                  <BenchmarkListItem
                    key={xBenchmark?.indicador_id || xIndex}
                    variant={"select"}
                    benchmark={xBenchmark}
                    checked={selectedOptions[xIndex] === true}
                    onChange={(e) =>
                      handleSelectChange(xIndex, e.target.checked)
                    }
                    metadata={{
                      qtd_ativos: xBenchmark.ativos.length,
                      ativos: xBenchmark.ativos.map((xItem) => {
                        const xAtivo = data.itens[xItem.ativo_id];
                        return xAtivo;
                      }),
                      cadastrado: xBenchmark.cadastrado, // pegar os detalhes dos itens
                    }}
                  />
                ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancelar
          </Button>

          <LoadingButton
            onClick={(e) =>
              handleStore(e, selectedOptions, xBenchmarks, benchmarks)
            }
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="save" size={16} />}
            size="small"
            sx={{ px: 2 }}
            type="submit"
            disabled={isSubmitting || loading || data.length === 0}
            loading={isSubmitting}
          >
            Salvar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default withMessage(BenchmarksSugestao);
